/**
 * Planet Breakdown — every planet, and the order teams arrived on it.
 *
 * Pivots the per-team planet lists in the snapshot into a per-PLANET view,
 * which is the one an admin actually asks for after a game ("who got Planet 6
 * first?"). Planets are grouped by graph depth, since that is what a "round"
 * means in Trailblazers — there is no configured round count.
 */

import { useMemo } from 'react';
import type { TrlTeamStat } from '@swtorbit/shared-socket-events';
import { StatCard } from './StatCard';
import { formatMs, ordinal } from './statsFormat';

interface PlanetRow {
  nodeId: string;
  label: string;
  depth: number | null;
  roundType: 'single' | 'triangulation';
  arrivals: {
    teamId: string;
    teamName: string;
    rank: number;
    points: number | null;
    timeOnPlanetMs: number | null;
    clearedBy: string | null;
    distanceM: number | null;
  }[];
}

export function TrlPlanetBreakdownCard({ teams }: { teams: TrlTeamStat[] }) {
  const planets = useMemo(() => {
    const byNode = new Map<string, PlanetRow>();

    for (const team of teams) {
      for (const planet of team.planets) {
        // Only a CLEARED planet has an arrival order to report.
        if (planet.rank == null) continue;
        let row = byNode.get(planet.nodeId);
        if (!row) {
          row = {
            nodeId: planet.nodeId,
            label: planet.label,
            depth: planet.depth,
            roundType: planet.roundType,
            arrivals: [],
          };
          byNode.set(planet.nodeId, row);
        }
        row.arrivals.push({
          teamId: team.teamId,
          teamName: team.name,
          rank: planet.rank,
          points: planet.pointsAwarded,
          timeOnPlanetMs: planet.timeOnPlanetMs,
          clearedBy: planet.clearedBy,
          distanceM: planet.distanceM,
        });
      }
    }

    for (const row of byNode.values()) row.arrivals.sort((a, b) => a.rank - b.rank);

    return [...byNode.values()].sort((a, b) => {
      const da = a.depth ?? Number.MAX_SAFE_INTEGER;
      const dbb = b.depth ?? Number.MAX_SAFE_INTEGER;
      if (da !== dbb) return da - dbb;
      return a.label.localeCompare(b.label, undefined, { numeric: true });
    });
  }, [teams]);

  return (
    <StatCard
      title="Planet Breakdown"
      icon="🪐"
      summary={`${planets.length} cleared`}
      isEmpty={planets.length === 0}
      emptyMessage="No planets were cleared in this game."
    >
      <div className="d-flex flex-column gap-xs">
        {planets.map((planet) => (
          <div key={planet.nodeId} className="d-flex flex-column gap-2xs">
            <div className="d-flex align-items-center gap-xs flex-wrap">
              <span className="text-white">{planet.label}</span>
              {planet.depth != null && (
                <span className="badge badge--sm badge--pill">Round {planet.depth}</span>
              )}
              {planet.roundType === 'triangulation' && (
                <span className="fs-xs text-slate">triangulation</span>
              )}
            </div>

            <div className="d-flex flex-column gap-2xs ps-sm">
              {planet.arrivals.map((a) => (
                <div key={a.teamId} className="d-flex gap-xs align-items-center fs-sm flex-wrap">
                  <span className="text-gold" style={{ minWidth: '2.5rem' }}>{ordinal(a.rank)}</span>
                  <span className="text-white flex-1" style={{ minWidth: '9rem' }}>{a.teamName}</span>
                  <span className="text-cyan" style={{ minWidth: '5rem' }}>{a.points ?? '—'} pts</span>
                  <span className="text-cyan" style={{ minWidth: '6rem' }}>{formatMs(a.timeOnPlanetMs)}</span>
                  {a.clearedBy && (
                    <span className="fs-xs text-slate">
                      dug by {a.clearedBy}
                      {a.distanceM != null && ` · ${a.distanceM.toFixed(1)}m`}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </StatCard>
  );
}
